export const STEPS = [
  {
    key: "identify",
    n: "01",
    label: "Identify",
    line: "Authenticate the principal and establish the organizational identity.",
  },
  {
    key: "delegate",
    n: "02",
    label: "Delegate",
    line: "Verify the agent’s delegated authority, purpose, scope, and limits.",
  },
  {
    key: "authorize",
    n: "03",
    label: "Authorize",
    line: "Evaluate the exact request against access and counterparty policy.",
  },
  {
    key: "exchange",
    n: "04",
    label: "Exchange",
    line: "Release only what policy authorizes, or refuse and record why.",
  },
  {
    key: "attest",
    n: "05",
    label: "Attest",
    line: "Sign the action, the decision, and the supporting context.",
  },
  {
    key: "verify",
    n: "06",
    label: "Verify",
    line: "The counterparty examines the claims. No vendor assertion required.",
  },
] as const;

export type StepKey = (typeof STEPS)[number]["key"];

export type Claim = { k: string; v: string };

export type StepRecord = {
  headline: string;
  detail: string;
  claims: Claim[];
};

export type Scenario = {
  id: string;
  title: string;
  blurb: string;
  verdict: "permit" | "deny";
  /** Last step index that executes. Permit is always 5. */
  settleAt: number;
  request: string;
  owner: {
    org: string;
    domain: string;
    principal: string;
    assurance: string;
    domainId: string;
  };
  requester: {
    org: string;
    agent: string;
    principal: string;
    purpose: string;
    expires: string;
    chain: string[];
  };
  evidence: { id: string; name: string; note: string }[];
  policy: string;
  code: string;
  reason: string;
  artifactId: string;
  records: Record<StepKey, StepRecord>;
};

const skipped = (why: string): StepRecord => ({
  headline: "Not reached",
  detail: why,
  claims: [],
});

export const SCENARIOS: Scenario[] = [
  {
    id: "security-review",
    title: "Security review",
    blurb: "A bank’s reviewer asks for two control narratives. Scope, tier, and freshness all hold.",
    verdict: "permit",
    settleAt: 5,
    request:
      "Disclose the SOC 2 Type II control narratives for CC6.1 and CC6.6, standard tier, purpose security-review.",
    owner: {
      org: "Northwind Cloud",
      domain: "northwind.cloud",
      principal: "Amira Shah, CISO",
      assurance: "Domain-verified",
      domainId: "td_northwind_prod",
    },
    requester: {
      org: "Helios Bank",
      agent: "procurement.reviewer",
      principal: "Jonah Ell, Third-Party Risk",
      purpose: "security-review",
      expires: "12 Nov 2026",
      chain: [
        "Helios CISO → TPR lead · vendor assurance",
        "TPR lead → procurement.reviewer · control narratives only",
      ],
    },
    evidence: [
      { id: "ev_cc61", name: "CC6.1 logical access", note: "Standard tier · fresh 12 days" },
      { id: "ev_cc66", name: "CC6.6 boundary protection", note: "Standard tier · fresh 12 days" },
    ],
    policy: `permit (
  principal == Agent::"helios.procurement.reviewer",
  action == Action::"disclose",
  resource
) when {
  resource.tier == "standard"
  && context.purpose == "security-review"
  && resource.fresh
};`,
    code: "PERMIT",
    reason: "Delegated purpose, disclosure tier, and evidence freshness all match Northwind policy.",
    artifactId: "rcpt_8c21e4",
    records: {
      identify: {
        headline: "Both organizations are who they claim.",
        detail:
          "Helios authenticates with DPoP-bound OAuth 2.1. Northwind’s trust domain is domain-verified. Neither party imports the other’s directory.",
        claims: [
          { k: "Requester", v: "Helios Bank · agent procurement.reviewer" },
          { k: "Accountable", v: "Jonah Ell, Third-Party Risk" },
          { k: "Evidence owner", v: "Northwind Cloud · td_northwind_prod" },
          { k: "Assurance", v: "Domain-verified · customer-held root" },
        ],
      },
      delegate: {
        headline: "Authority is explicit, and it narrows.",
        detail:
          "Two hops. Each grant is smaller than its parent. The agent may request control narratives. It may not request raw tenant data. The grant expires 12 Nov 2026.",
        claims: [
          { k: "Chain depth", v: "2 of 3" },
          { k: "Purpose", v: "security-review" },
          { k: "Parameter limit", v: "Control narratives · standard tier" },
          { k: "Revocation", v: "Live · not revoked" },
        ],
      },
      authorize: {
        headline: "Cedar permits this exact request.",
        detail:
          "Governed access runs before any retrieval. The decision is bound to this agent, this purpose, and these two evidence objects.",
        claims: [
          { k: "Decision", v: "permit · dec_19af" },
          { k: "Policy", v: "northwind.disclose.standard" },
          { k: "Counterparty", v: "Helios allowed for security-review" },
        ],
      },
      exchange: {
        headline: "Two excerpts leave. The report stays.",
        detail:
          "Northwind releases the authorized narratives with citations. The full SOC 2 and every other evidence object remain inside the owner’s boundary.",
        claims: [
          { k: "Released", v: "ev_cc61, ev_cc66" },
          { k: "Withheld", v: "Full report, customer data, restricted tier" },
          { k: "Citations", v: "Each sentence points at its excerpt" },
        ],
      },
      attest: {
        headline: "The decision is now a signed record.",
        detail:
          "Northwind’s customer-held key signs the answer, the Cedar decision, the evidence hashes, and Helios’s acknowledgment slot.",
        claims: [
          { k: "Artifact", v: "rcpt_8c21e4" },
          { k: "Key", v: "kid nw-root-2026 · Ed25519" },
          { k: "Binds", v: "identity + delegation + decision + excerpts" },
        ],
      },
      verify: {
        headline: "Helios checks the record itself.",
        detail:
          "Signature, key, evidence hashes, and the authorization claim verify against the transparency log. Bifrost does not have to be believed.",
        claims: [
          { k: "Signature", v: "Valid" },
          { k: "Log", v: "Inclusion proof · seq 44821" },
          { k: "Vendor assertion", v: "Not required" },
        ],
      },
    },
  },
  {
    id: "stale-evidence",
    title: "Stale evidence",
    blurb: "The same parties. The pentest is outside the freshness window, so the exchange stops.",
    verdict: "deny",
    settleAt: 2,
    request: "Disclose the latest penetration-test summary for the production boundary, standard tier.",
    owner: {
      org: "Northwind Cloud",
      domain: "northwind.cloud",
      principal: "Amira Shah, CISO",
      assurance: "Domain-verified",
      domainId: "td_northwind_prod",
    },
    requester: {
      org: "Helios Bank",
      agent: "procurement.reviewer",
      principal: "Jonah Ell, Third-Party Risk",
      purpose: "security-review",
      expires: "12 Nov 2026",
      chain: [
        "Helios CISO → TPR lead · vendor assurance",
        "TPR lead → procurement.reviewer · control narratives only",
      ],
    },
    evidence: [
      { id: "ev_pentest", name: "Pentest 2024-Q1", note: "Age 410 days · window 90" },
    ],
    policy: `forbid (
  principal,
  action == Action::"disclose",
  resource
) when {
  resource.age_days > resource.freshness_window
};`,
    code: "EVIDENCE_STALE",
    reason:
      "The only matching report is 410 days old. Policy refuses disclosure rather than release a stale assurance.",
    artifactId: "ref_3b70aa",
    records: {
      identify: {
        headline: "Identity holds. The evidence does not.",
        detail: "Both organizations authenticate. The failure is not who is asking. It is whether the evidence may still be disclosed.",
        claims: [
          { k: "Requester", v: "Helios Bank · procurement.reviewer" },
          { k: "Owner", v: "Northwind Cloud" },
          { k: "Assurance", v: "Domain-verified" },
        ],
      },
      delegate: {
        headline: "The grant covers this class of request.",
        detail: "Purpose is security-review. The delegation has not expired and has not been revoked. Authority is not the defect.",
        claims: [
          { k: "Purpose", v: "security-review" },
          { k: "Expires", v: "12 Nov 2026" },
          { k: "Revoked", v: "No" },
        ],
      },
      authorize: {
        headline: "Refused before anything is retrieved.",
        detail:
          "Freshness is checked prior to retrieval. A 410-day pentest cannot satisfy a 90-day window. The refusal is the decision.",
        claims: [
          { k: "Code", v: "EVIDENCE_STALE" },
          { k: "Age", v: "410 days · window 90" },
          { k: "Escalation", v: "A person at Northwind may release a newer report" },
        ],
      },
      exchange: skipped("Retrieval never starts. Stale evidence is not a partial answer."),
      attest: skipped("No disclosure is signed. The refusal itself is the record."),
      verify: skipped("Helios can verify the refusal, not a report that was never released."),
    },
  },
  {
    id: "out-of-scope",
    title: "Out of scope",
    blurb: "The agent asks for customer identifiers. Its delegation never granted that.",
    verdict: "deny",
    settleAt: 1,
    request: "Export production tenant logs including account identifiers for Helios’s diligence file.",
    owner: {
      org: "Northwind Cloud",
      domain: "northwind.cloud",
      principal: "Amira Shah, CISO",
      assurance: "IdP-verified",
      domainId: "td_northwind_prod",
    },
    requester: {
      org: "Helios Bank",
      agent: "procurement.reviewer",
      principal: "Jonah Ell, Third-Party Risk",
      purpose: "security-review",
      expires: "12 Nov 2026",
      chain: [
        "Helios CISO → TPR lead · vendor assurance, no customer data",
        "TPR lead → procurement.reviewer · control narratives only",
      ],
    },
    evidence: [
      { id: "ev_logs", name: "Tenant audit logs", note: "Restricted · contains identifiers" },
    ],
    policy: `forbid (
  principal == Agent::"helios.procurement.reviewer",
  action == Action::"disclose",
  resource
) unless {
  resource.class in principal.delegation.classes
};`,
    code: "DELEGATION_SCOPE",
    reason:
      "The chain narrows to control narratives. Account identifiers were never in scope, so the agent cannot ask for them.",
    artifactId: "ref_91cd02",
    records: {
      identify: {
        headline: "The agent is authentic. That is not authority.",
        detail:
          "An authenticated connection does not establish what an agent is allowed to do. Identity resolves. The grant is checked next.",
        claims: [
          { k: "Agent", v: "procurement.reviewer" },
          { k: "Principal", v: "Jonah Ell" },
          { k: "Owner", v: "Northwind Cloud · IdP-verified" },
        ],
      },
      delegate: {
        headline: "The request is outside every grant in the chain.",
        detail:
          "Level one already excludes customer data. Level two narrows further, to control narratives. There is no third grant that widens it. Bifrost refuses here.",
        claims: [
          { k: "Code", v: "DELEGATION_SCOPE" },
          { k: "Asked for", v: "Tenant logs with account identifiers" },
          { k: "Granted", v: "Control narratives · standard tier" },
          { k: "Widening", v: "Not allowed · child cannot exceed parent" },
        ],
      },
      authorize: skipped("Policy is not evaluated. There is no authority to evaluate it under."),
      exchange: skipped("No evidence moves."),
      attest: skipped("The refusal record is enough. Nothing was disclosed."),
      verify: skipped("A counterparty can confirm the refusal code and the chain that produced it."),
    },
  },
  {
    id: "source-conflict",
    title: "Source conflict",
    blurb: "Two approved sources disagree on encryption at rest. Bifrost will not pick a side.",
    verdict: "deny",
    settleAt: 3,
    request: "State the production encryption-at-rest algorithm, with a citation, for a supplier qualification.",
    owner: {
      org: "Northwind Cloud",
      domain: "northwind.cloud",
      principal: "Amira Shah, CISO",
      assurance: "Domain-verified",
      domainId: "td_northwind_prod",
    },
    requester: {
      org: "Helios Bank",
      agent: "supplier.qualifier",
      principal: "Lina Ortiz, Procurement",
      purpose: "supplier-qualification",
      expires: "2 Dec 2026",
      chain: ["Helios CPO → Procurement · supplier qualification, standard tier"],
    },
    evidence: [
      { id: "ev_matrix", name: "Control matrix", note: "Says AES-256 · fresh" },
      { id: "ev_addendum", name: "Architecture addendum", note: "Says AES-128 legacy store · fresh" },
    ],
    policy: `permit (
  principal,
  action == Action::"answer",
  resource
) when {
  context.sources_agree
  && context.purpose == "supplier-qualification"
};`,
    code: "SOURCE_CONFLICT",
    reason:
      "The control matrix and the architecture addendum disagree. A generated answer would have to invent agreement. Bifrost refuses.",
    artifactId: "ref_c4e118",
    records: {
      identify: {
        headline: "A different agent, a narrower job.",
        detail: "Helios Procurement is asking as supplier.qualifier. Northwind still owns the evidence and the signing key.",
        claims: [
          { k: "Agent", v: "supplier.qualifier" },
          { k: "Principal", v: "Lina Ortiz, Procurement" },
          { k: "Purpose", v: "supplier-qualification" },
        ],
      },
      delegate: {
        headline: "The delegation covers a factual answer.",
        detail: "One hop, standard tier, supplier qualification, unexpired. Authority is intact. The evidence is the problem.",
        claims: [
          { k: "Chain", v: "1 hop · does not widen" },
          { k: "Expires", v: "2 Dec 2026" },
          { k: "Class", v: "Standard-tier factual answers" },
        ],
      },
      authorize: {
        headline: "Each source would have been disclosable alone.",
        detail:
          "Policy would permit a cited answer if the sources agreed. They do not, so the permit’s when-clause fails closed.",
        claims: [
          { k: "Matrix", v: "AES-256 at rest" },
          { k: "Addendum", v: "AES-128 on the legacy object store" },
          { k: "Agreement", v: "False" },
        ],
      },
      exchange: {
        headline: "No answer is safer than a confident contradiction.",
        detail:
          "Citation checking refuses to bind one sentence to two incompatible excerpts. Nothing is released. The conflict is the record.",
        claims: [
          { k: "Code", v: "SOURCE_CONFLICT" },
          { k: "Released", v: "Nothing" },
          { k: "Owner next step", v: "Reconcile the two sources, then re-ask" },
        ],
      },
      attest: skipped("There is no answer to sign. The refusal is recorded at the exchange step."),
      verify: skipped("Helios can verify that no claim about the algorithm was issued."),
    },
  },
];

export function scenarioById(id: string): Scenario {
  return SCENARIOS.find((s) => s.id === id) ?? (SCENARIOS[0] as Scenario);
}

export function stepAt(index: number): (typeof STEPS)[number] {
  return STEPS[Math.min(Math.max(index, 0), STEPS.length - 1)] as (typeof STEPS)[number];
}
